import { useContext } from "react";
import { useSelector } from "react-redux";
import classNames from "classnames";

import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";

import { ProductListingContext } from "@components/store/products/listing/listing-provider";
import ProductFilterDropdown from "@components/store/products/listing-header/filter-dropdown";
import ProductSortDropdown from "@components/store/products/listing-header/sort-dropdown";
import AddOndemandModal from "@components/modals/add-ondemand";
import ShippingBanner from "@sharedComponents/banner/shipping";
import GlobalProductSearch from "@sharedComponents/global-search/global-product-search";

import { getIsLockerShop } from "@selectors/store";

import { useModal } from "@stadium/hooks/lib/use-modal";
import { useStoreContext } from "@components/store/provider";
import { useFilter } from "@stadium/shared/src/hooks/use-filter";
import { LinkButton } from "@stadium/shared/src/components/shared/button";
import { CATALOG_REQUEST_ITEM_TYPE_FORM_LINK } from "@stadium/shared/src/constants/stores/routes";

import { pluralizeWord } from "@stadium/helpers/string";
import { formatNumber } from "@stadium/helpers/number";

import { ADD_ONDEMAND_MODAL } from "@constants/modal-types";
import { PRODUCT_LISTING_HEADER_ANCHOR_ID } from "@stadium/shared/src/helpers/product-listing-scroll";
import { useCreateSwagContext } from "@contexts/create-swag-context";

import useShowNewHeader from "@hooks/use-show-new-header";

import styles from "./listing-header.module.scss";

const FilterDropdown = ({ isNewSwagmagicHost, isDesktopDevice, filterConfig }) => {
  if (!isNewSwagmagicHost) return <ProductFilterDropdown />;
  return isDesktopDevice ? (
    <ProductFilterDropdown filterConfig={filterConfig} />
  ) : (
    <div className={styles.filterDropdownWrapperV2}>
      <span className={styles.filterDropdownIconV2}></span>
      <ProductFilterDropdown filterConfig={filterConfig} />
    </div>
  );
};

const ListingHeader = ({
  totalProducts,
  showFilters = true,
  searchedFor = "",
  showSortDropdown = true,
  source,
  containerClassName = "",
  title = "",
  categoryLabel = "",
  isFetchingProducts,
  filterConfig,
  renderAddAllToCatalogCta,
}) => {
  const { setFilterToDefault } = useContext(ProductListingContext);

  const isLockerShop = useSelector(getIsLockerShop);

  const showNewHeader = useShowNewHeader();

  const { isPreviewCatalogMode, isNewSwagmagicHost } = useStoreContext();
  const { isDesktopDevice, isMobileDevice } = useContext(CurrentDeviceContext);
  const { isSwagCreationShopFlow } = useCreateSwagContext();
  const { renderModal } = useModal(ADD_ONDEMAND_MODAL, AddOndemandModal);

  const { clear: clearFilter } = useFilter(filterConfig || []);

  const showExpertBtn = isLockerShop && !isDesktopDevice;

  const handleSetFilterToDefault = () => {
    if (isNewSwagmagicHost) {
      return clearFilter();
    }
    setFilterToDefault();
  };

  const renderResultsCount = () => {
    if (totalProducts === 0) return null;

    return (
      <div
        className={classNames(styles.resultBar, {
          [styles.displayHidden]: isFetchingProducts,
        })}
      >
        <div className={styles.titleBar}>
          <span
            className={`${styles.resultData} ${
              isNewSwagmagicHost ? styles.resultDataV2 : ""
            } search-result-data`}
          >
            {formatNumber(totalProducts)} {pluralizeWord("product", { count: totalProducts })}
          </span>
        </div>
      </div>
    );
  };

  // When filter v2 is on: show "Category | X results" in header; category comes from menu_category filter or current category
  const renderCategoryAndResults = () => {
    if (!categoryLabel) return null;
    const resultsText =
      totalProducts === 0
        ? null
        : `${formatNumber(totalProducts)} ${pluralizeWord("result", { count: totalProducts })}`;

    return (
      <div
        className={classNames(styles.resultBar, styles.categoryResultsLine, {
          [styles.resultBarCategoryName]: isNewSwagmagicHost,
        })}
      >
        <div className={styles.titleBar}>
          <span className={styles.categoryName}>{categoryLabel}</span>
          {resultsText != null && !isFetchingProducts && (
            <>
              <span className={styles.categoryResultsSeparator}> | </span>
              <span className={styles.resultData}>{resultsText}</span>
            </>
          )}
        </div>
      </div>
    );
  };

  const hideResultsCount = showNewHeader && isMobileDevice;

  return (
    <>
      {isLockerShop && !isNewSwagmagicHost && isMobileDevice && (
        <div className={styles.shippingBannerCont}>
          <ShippingBanner showAsBanner={false} />
        </div>
      )}
      <article
        id={PRODUCT_LISTING_HEADER_ANCHOR_ID}
        className={`${styles.rowListHeaderWrap} ${containerClassName}  ${
          isPreviewCatalogMode ? styles.searchVisible : ""
        } ${isNewSwagmagicHost ? styles.rowListHeaderWrapV2 : ""} ${
          styles.listingHeaderScrollAnchor
        } ${isSwagCreationShopFlow ? styles.swagCreationShopFlow : ""}`}
      >
        {searchedFor && !hideResultsCount && Boolean(totalProducts) ? (
          <div className={styles.searchHeadText}>
            Showing {`${pluralizeWord("result", { count: totalProducts })} for "${searchedFor}"`}
          </div>
        ) : (
          ""
        )}

        <div
          className={` ${showNewHeader ? styles.headerNewTopBlock : styles.topBlock} ${
            searchedFor && !showNewHeader ? styles.withSearchTopBlock : ""
          } ${isPreviewCatalogMode ? styles.previewModeTopBlock : ""} ${
            isSwagCreationShopFlow ? styles.swagTopBlock : ""
          }`}
        >
          <div className={styles.resultCountCol}>
            <div
              className={classNames({
                [styles.titleWithExpertRow]: showExpertBtn,
              })}
            >
              {isNewSwagmagicHost && categoryLabel ? (
                renderCategoryAndResults()
              ) : (
                <>
                  {title && <div className={styles.productTitle}>{title}</div>}
                  {renderResultsCount()}
                </>
              )}
              {showExpertBtn && (
                <LinkButton
                  href={CATALOG_REQUEST_ITEM_TYPE_FORM_LINK}
                  styleType="primary"
                  className={styles.askExpertHeaderBtn}
                  target="_blank"
                >
                  <span className={`icon-message-square ${styles.askExpertHeaderBtnIcon}`} /> Ask An
                  Expert
                </LinkButton>
              )}
            </div>
          </div>
          {renderAddAllToCatalogCta?.()}

          <div
            className={classNames(
              styles.filterSortCol,
              showNewHeader ? styles.filterNewSortCol : styles.filterOldSortCol,
              { [styles.filterSortNewColV2]: isNewSwagmagicHost },
            )}
          >
            {isDesktopDevice && isPreviewCatalogMode && <GlobalProductSearch />}
            {showSortDropdown && <ProductSortDropdown source={source} />}
            {showFilters && (
              <FilterDropdown
                isNewSwagmagicHost={isNewSwagmagicHost}
                isDesktopDevice={isDesktopDevice}
                filterConfig={filterConfig}
              />
            )}
          </div>
        </div>
        {renderModal(
          {
            data: {
              onClick: handleSetFilterToDefault,
            },
          },
          false,
        )}
      </article>
    </>
  );
};

export default ListingHeader;
